import { isDeepStrictEqual } from "node:util";
import { createSelectorCreator, lruMemoize } from "reselect";
import { ALPHA_EQUAL_A_ZERO, DRUCKPFAHL, ZUGPFAHL } from "../constants";
import { selectAashtoAll } from "../store/slices/aashtoSlice";
import {
  selectAnpassungsfaktor,
  selectAnzahlPfaehle,
  selectBSP,
  selectBemessungspfahlwiderstand,
  selectBetonzugfestigkeit,
  selectDruckfestigkeitZement,
  selectDruckpfahlgruppe,
  selectGroesseresPfahlraster,
  selectKleineresPfahlraster,
  selectLa,
  selectLb,
  selectLongestPfahlValue,
  selectLongestPfahlValuePlusExtension,
  selectMinimalePfahllaenge,
  selectModelFaktor,
  selectPerimeterPileGroup,
  selectPfahllaengeGewaehltInBoden,
  selectPfahlneigungZurVertikalen,
  selectPileCapExtension,
  selectReibungswinkelBoden,
  selectSchichtenCalculated,
  selectSchichteninput,
  selectStaendigeDestabilisierendeEinwirkungen,
  selectStaendigeStabilisierendeEinwirkungen,
  selectTeilsicherheitsbeiwert as selectTeilsicherheitsbeiwertAeussere,
  selectTeilsicherheitsbeiwertTransmission,
  selectVeraenderlicheDestabilisierendeEinwirkungen,
  selectVerbundbedingungenEta1,
  selectWichteBoden,
  selectZgruppeLeft,
  selectZgruppeLreq,
  selectZgruppeLreqIsValid,
  selectZgruppeRight,
} from "../store/slices/aeussereSlice";
import {
  selectGuenstigeStaendigeEinwirkungen,
  selectStaendigeEinwirkungen,
  selectStaendigeEinwirkungenAusErdruhedruck,
  selectUnguenstigeVeraenderlicheEinwirkungen,
} from "../store/slices/angepassteBemessungslastSlice";
import { selectBelastungsartUndRichtung } from "../store/slices/belastungsartUndRichtungSlice";
import {
  BemessungenMikropfaehle,
  selectBemessungenMikropfaehle,
} from "../store/slices/bemessungenMikropfaehleSlice";
import { selectBemessungslastGruppeGesamt } from "../store/slices/bemessungslastGruppeGesamtSlice";
import { selectBemessungslast } from "../store/slices/bemessungslastSlice";
import { selectBohrkronendurchmesser } from "../store/slices/bohrkronendurchmesserSlice";
import {
  selectLogoImageBase64Url,
  selectPreparedBy,
  selectProject,
} from "../store/slices/deckblattSlice";
import { selectFHWA } from "../store/slices/fhwaSlice";
import { selectEdp } from "../store/slices/epdSlice";
import { selectFrontendZustand } from "../store/slices/frontendZustandSlice";
import {
  BemessungssituationState,
  selectAnzahlLastwechsel,
  selectAufweitungNnachDIN_SPEC_18539,
  selectBemessungssituation,
  selectBiegesteifigkeit,
  selectDickeWeicheBodenschicht,
  selectKnickkoeffizient,
  selectKoeffizientNachGudehus,
  selectKoeffizientNachWenz,
  selectQuerschnittsnachweis,
  selectSchwingungsObergrenze,
  selectSchwingungsUntergrenze,
  selectStaerkeDesZylinders,
  selectTeilsicherheitsbeiwertFuerErmuedung,
  selectTeilsicherheitsbeiwertFuerErmuedungslastenDinEN1991,
  selectTeilsicherheitsbeiwertFuerGeotechnischenKenngroessen,
  selectTeilsicherheitsbeiwert as selectTeilsicherheitsbeiwertInnere,
  selectTeilsicherheitsbeiwertZement,
  selectUndraenierteScherfestigkeit,
  selectVerpresskoerperdurchmesser,
} from "../store/slices/innereSlice";
import { selectWerkstoffUebergangsrohr } from "../store/slices/pileHeadConstructionSlice";
import { selectTragglied } from "../store/slices/traggliedSlice";
import { berechneErmuedung } from "./Inner/berechneErmuedung.service";
import BerechneInnereTragfaehigkeit from "./Inner/berechneInnereTragfaehigkeit.service";
import { berechneKnickenDinEN1993 } from "./Inner/berechneKnickenDinEN1993.service";
import { berechneKnickenOfner } from "./Inner/berechneKnickenOfner.service";
import { berechneSchichten } from "./Schichten/berechneSchichten";
import berechneAussere from "./berechneAussere.service";
import {
  berechneAchsabstand,
  berechneC,
  berechneDurchmesserDesRohres,
  berechneWandDicke,
} from "./berechnePileHead";
import {
  berechneBondStrength,
  berechneUebertragungslaenge,
  berechneVerbundfestigkeit,
} from "./berechneTransmission";
import {
  berechneGewichtkraftDesAngehaengtenBodens,
  berechneLeft,
  berechneLreq,
  berechneRight,
} from "./berechneZGruppe.service";
import { berechneVerformungsbegrenzung } from "./knickenBerechnungen.service";

// create a "selector creator" that uses lodash.isequal instead of ===
const createDeepEqualSelector = createSelectorCreator(lruMemoize, isDeepStrictEqual);

// Parameter order is important!
export const selectAllResults = createDeepEqualSelector(
  [
    selectFHWA,
    selectAashtoAll,
    selectEdp,
    selectBemessungenMikropfaehle,
    selectProject,
    selectPreparedBy,
    selectWerkstoffUebergangsrohr,
    selectStaendigeDestabilisierendeEinwirkungen,
    selectVeraenderlicheDestabilisierendeEinwirkungen,
    selectStaendigeStabilisierendeEinwirkungen,
    selectWichteBoden,
    selectAnpassungsfaktor,
    selectReibungswinkelBoden,
    selectKleineresPfahlraster,
    selectGroesseresPfahlraster,
    selectAnzahlPfaehle,
    selectBSP,
    selectBetonzugfestigkeit,
    selectTeilsicherheitsbeiwertTransmission,
    selectDruckfestigkeitZement,
    selectVerbundbedingungenEta1,
    selectDruckpfahlgruppe,
    selectPerimeterPileGroup,
    selectLa,
    selectLb,
    selectBemessungslastGruppeGesamt,
    selectLongestPfahlValuePlusExtension,
    selectLongestPfahlValue,
    selectPileCapExtension,
    selectModelFaktor,
    selectSchichteninput,
    selectTeilsicherheitsbeiwertAeussere,
    selectPfahlneigungZurVertikalen,
    selectSchwingungsObergrenze,
    selectSchwingungsUntergrenze,
    selectTeilsicherheitsbeiwertFuerErmuedungslastenDinEN1991,
    selectTeilsicherheitsbeiwertFuerErmuedung,
    selectAnzahlLastwechsel,
    selectDickeWeicheBodenschicht,
    selectKoeffizientNachGudehus,
    selectUndraenierteScherfestigkeit,
    selectKoeffizientNachWenz,
    selectVerpresskoerperdurchmesser,
    selectKnickkoeffizient,
    selectBiegesteifigkeit,
    selectFrontendZustand,
    selectMinimalePfahllaenge,
    selectPfahllaengeGewaehltInBoden,
    selectBemessungspfahlwiderstand,
    selectSchichtenCalculated,
    selectAufweitungNnachDIN_SPEC_18539,
    selectTeilsicherheitsbeiwertInnere,
    selectBelastungsartUndRichtung,
    selectBemessungslast,
    selectTragglied,
    selectBohrkronendurchmesser,
    selectBemessungssituation,
    selectStaendigeEinwirkungen,
    selectUnguenstigeVeraenderlicheEinwirkungen,
    selectStaendigeEinwirkungenAusErdruhedruck,
    selectGuenstigeStaendigeEinwirkungen,
    selectStaerkeDesZylinders,
    selectTeilsicherheitsbeiwertZement,
    selectTeilsicherheitsbeiwertFuerGeotechnischenKenngroessen,
    selectQuerschnittsnachweis,
    selectLogoImageBase64Url,
    selectZgruppeLeft,
    selectZgruppeRight,
    selectZgruppeLreq,
    selectZgruppeLreqIsValid,
    (_state, parameter) => parameter,
  ],
  (
    fhwaAll,
    aashtoAll,
    edpAll,
    bemessungenMikropfaehle,
    project,
    preparedBy,
    werkstoffUebergangsrohr,
    staendigeDestabilisierendeEinwirkungen,
    veraenderlicheDestabilisierendeEinwirkungen,
    staendigeStabilisierendeEinwirkungen,
    wichteBoden,
    anpassungsfaktor,
    reibungswinkelBoden,
    kleineresPfahlraster,
    groesseresPfahlraster,
    anzahlPfaehle,
    bsp,
    betonzugfestigkeit,
    teilsicherheitsbeiwertTransmission,
    druckfestigkeitZement,
    verbundbedingungenEta1,
    druckpfahlgruppe,
    perimeterPileGroup,
    la,
    lb,
    bemessungslastGruppeGesamt,
    longestPfahlValuePlusExtension,
    longestPfahlValue,
    pileCapExtension,
    modelFaktor,
    schichteninput,
    teilsicherheitsbeiwertAeussere,
    pfahlneigungZurVertikalen,
    schwingungsObergrenze,
    schwingungsUntergrenze,
    teilsicherheitsbeiwertFuerErmuedungslastenDinEN1991,
    teilsicherheitsbeiwertFuerErmuedung,
    anzahlLastwechsel,
    dickeWeicheBodenschicht,
    koeffizientNachGudehus,
    undraenierteScherfestigkeit,
    koeffizientNachWenz,
    verpresskoerperdurchmesser,
    knickkoeffizient,
    biegesteifigkeit,
    frontendZustand,
    minimalePfahllaenge,
    pfahllaengeGewaehltInBoden,
    bemessungspfahlwiderstand,
    schichtenCalculated,
    aufweitungNnachDIN_SPEC_18539,
    teilsicherheitsbeiwertTragglied,
    belastungsartUndRichtung,
    bemessungslast,
    tragglied,
    bohrkronendurchmesser,
    bemessungssituation,
    staendigeEinwirkungen,
    unguenstigeVeraenderlicheEinwirkungen,
    staendigeEinwirkungenAusErdruhedruck,
    guenstigeStaendigeEinwirkungen,
    staerkeDesZylinders,
    teilsicherheitsbeiwertZement,
    teilsicherheitsbeiwertFuerGeotechnischenKenngroessen,
    querschnittsnachweis,
    logoImageBase64Url,
    zGruppeLeft,
    zGruppeRight,
    zGruppeLreq,
    zGruppeLreqIsValid,
    parameter
  ) => {
    const { traggliedDaten } = parameter;

    // #region Innere
    let innereTragfaehigkeitsWerte = BerechneInnereTragfaehigkeit({
      bemessungenMikropfaehle,
      bemessungssituation,
      tragglied,
      bemessungslast,
      bohrkronendurchmesser,
      belastungsartUndRichtung,
      traggliedDaten: traggliedDaten,
      teilsicherheitsbeiwert: teilsicherheitsbeiwertTragglied,
    });

    if (
      bemessungssituation === BemessungssituationState.TEMPORÄR ||
      bemessungenMikropfaehle === BemessungenMikropfaehle.EUROCODE_7
    ) {
      innereTragfaehigkeitsWerte.Csol = 20;
    }

    // #region Angepasste Bemessungslast
    let bemessungslastBerechnet = {};
    if (frontendZustand.angepassteBemessungslast) {
      bemessungslastBerechnet = {
        staendigeEinwirkungen: staendigeEinwirkungen,
        unguenstigeVeraenderlicheEinwirkungen: unguenstigeVeraenderlicheEinwirkungen,
        staendigeEinwirkungenAusErdruhedruck: staendigeEinwirkungenAusErdruhedruck,
        guenstigeStaendigeEinwirkungen: guenstigeStaendigeEinwirkungen,
      };
    }
    // #endregion

    // #region Knicken
    let KnickenResults = { Ofner: {}, dinEN: {} };
    if (frontendZustand.knickenCheckbox) {
      // #region Ofner
      if (frontendZustand.knickenTypeRadio == 1) {
        let result = berechneKnickenOfner(
          biegesteifigkeit,
          Number(knickkoeffizient),
          traggliedDaten,
          verpresskoerperdurchmesser,
          teilsicherheitsbeiwertTragglied,
          koeffizientNachWenz,
          undraenierteScherfestigkeit,
          koeffizientNachGudehus,
          Number(dickeWeicheBodenschicht),
          bemessungslast,
          teilsicherheitsbeiwertFuerGeotechnischenKenngroessen,
          teilsicherheitsbeiwertZement
        );
        let verformungsbegrenzung = berechneVerformungsbegrenzung(
          result.verformungsamplitude,
          result.elastischerBereichDerSeitlichenBettung
        );

        let ParameterStahltragglied = {
          Querschnitt: result.berechneteKnickenWerte?.traggliedDaten?.querschnitt,
          Innendurchmesser: result.berechneteKnickenWerte?.traggliedDaten?.knicken.innendurchmesser,
          aeußererDurchmesser: result.berechneteKnickenWerte?.aussendurchmesser,
          Dehnsteifigkeit: result.berechneteKnickenWerte?.traggliedDaten?.knicken.dehnsteifigkeit,
          Elastizitaetsmodul: result.berechneteKnickenWerte?.elastizitaetsmodul,
          Traegheitsmoment: result.berechneteKnickenWerte?.traegheitsmoment,
          Biegesteifigkeit: result.berechneteKnickenWerte?.traggliedDaten?.knicken.biegesteifigkeit,
          Wiederstandsmoment: result.berechneteKnickenWerte?.widerstandsmoment,
          Imperfektionsbeiwert: ALPHA_EQUAL_A_ZERO,
          charakteristischeFestigkeit: result.berechneteKnickenWerte.charakteristischeFestigkeit,
          Teilsicherheitsbeiwert: teilsicherheitsbeiwertTragglied,
        };
        let ParameterVerpresskoerper = {
          Verpresskoerperdurchmesser: verpresskoerperdurchmesser,
          QuerschnittVerpresskoerper: result.Aci,
          StaerkeDesZylinders: staerkeDesZylinders,
          TeilsicherheitsbeiwertZement: teilsicherheitsbeiwertZement,
        };
        let TragfaehigkeitenMikropfahl = {
          BemessungswertDerNormalkraftTragfaehigkeit: result.normalkrafttragfaehigkeit,
          BemessungswertDerMomentenTragfaehigkeit: result.elastischeMomententragfaehigkeit,
        };
        let ParameterBaugrund = {
          DickeWeicheBodenschicht: dickeWeicheBodenschicht,
          undraenierteScherfestigkeit: undraenierteScherfestigkeit,
          TeilsicherheitsbeiwertFuerGeotechnischenKenngroeßen:
            teilsicherheitsbeiwertFuerGeotechnischenKenngroessen,
          KoeffizientNachWenz: koeffizientNachWenz,
          KoeffizientNachGudehus: koeffizientNachGudehus,
        };
        let Nachweise = {
          FließdruckDerSeitlichenBettung: result.qy_calc,
          elastischerBereichDerSeitlichenBettung: result.elastischerBereichDerSeitlichenBettung,
          Bettungsziffer: result.c_calc,
          AnzahlDerHalbwellen: result.m_calc,
          Halbwellenlaenge: result.lhw_calc,
          SchlankheitDesErsatzstabes: result.lamba1_calc,
          KnicklastDesErsatzstabes: result.nki_calc,
          KnicklastEinesGebettetenPfahls: result.knicklastDesGebettetenPfahls,
          Imperfektionsamplitude: result.ersatzimperfektionsamplitude,
          BiegemomentDesPfahls: result.biegemomentdesPfahls,
          Verformungsamplitude: result.verformungsamplitude,
          Querschnittsnachweis: querschnittsnachweis,
          Verformungsbegrenzung: verformungsbegrenzung,
        };

        KnickenResults = {
          Ofner: {
            ParameterStahltragglied,
            ParameterVerpresskoerper,
            TragfaehigkeitenMikropfahl,
            ParameterBaugrund,
            Nachweise,
          },
        };
      }
      // #endregion

      // #region DinEN
      if (frontendZustand.knickenTypeRadio == 0) {
        let result = berechneKnickenDinEN1993(
          Number(knickkoeffizient),
          traggliedDaten,
          dickeWeicheBodenschicht,
          Number(bemessungslast),
          teilsicherheitsbeiwertTragglied
        );

        let ParameterStahltragglied = {
          Querschnitt: result.berechneteKnickenWerte?.traggliedDaten?.querschnitt,
          Innendurchmesser: result.berechneteKnickenWerte?.traggliedDaten?.knicken.innendurchmesser,
          aeußererDurchmesser: result.berechneteKnickenWerte?.aussendurchmesser,
          Dehnsteifigkeit: result.berechneteKnickenWerte?.traggliedDaten?.knicken.dehnsteifigkeit,
          Elastizitaetsmodul: result.berechneteKnickenWerte?.elastizitaetsmodul,
          Traegheitsmoment: result.berechneteKnickenWerte?.traegheitsmoment,
          Biegesteifigkeit: result.berechneteKnickenWerte?.traggliedDaten?.knicken.biegesteifigkeit,
          Imperfektionsbeiwert: ALPHA_EQUAL_A_ZERO,
          charakteristischeFestigkeit: result.berechneteKnickenWerte.charakteristischeFestigkeit,
          Teilsicherheitsbeiwert: teilsicherheitsbeiwertTragglied,
        };

        let Nachweise = {
          dickeWeicheBodenschicht: dickeWeicheBodenschicht,
          knicklaengenbeiwert: knickkoeffizient,
          knicklaenge: result.berechneteKnickenWerte.kritischeKnicklaenge,
          traegheitsradius: result.berechneteKnickenWerte.traegheitsradius,
          bezugschlankheitsgrad: result.berechneteKnickenWerte.bezugsschlankheitsgrad,
          bezogenerSchlankheitsgrad: result.berechneteKnickenWerte.bezogenerSchlankheitsgrad,
          Abminderungsfaktor: {
            abminderungsfaktor: result.abminderungsfaktor,
            phi: result.phi_calc,
          },
          bemessungswertWiderstandeBiegeknicken: result.biegeknickbeanspruchbarkeit,
          ausnutzungs: result.ausnutzung,
        };

        KnickenResults = {
          dinEN: {
            ParameterStahltragglied,
            Nachweise,
          },
        };
      }
      // #endregion
    }
    //#endregion

    // #region Ermuedung
    let ermuedungResults = {};
    if (frontendZustand.ermuedungCheckbox) {
      let result = berechneErmuedung(
        traggliedDaten?.querschnitt,
        Number(anzahlLastwechsel),
        teilsicherheitsbeiwertFuerErmuedung,
        schwingungsObergrenze,
        schwingungsUntergrenze,
        Number(teilsicherheitsbeiwertFuerErmuedungslastenDinEN1991)
      );

      ermuedungResults = {
        schwingungsObergrenze: schwingungsObergrenze,
        schwingungsUntergrenze: schwingungsUntergrenze,
        teilsicherheitsbeiwertDinEN: teilsicherheitsbeiwertFuerErmuedungslastenDinEN1991,
        bemessungswertSchwingungsgrenze: result.upperLower,
        anzahlBelastungszyklen: anzahlLastwechsel,
        charakteristischeSpannungsschwingbreite: result.calc,
        querschnitt: traggliedDaten?.querschnitt,
        charakteristischeSchwingbreite: result.Rk,
        teilsicherheitsbeiwertErmuedung: teilsicherheitsbeiwertFuerErmuedung,
        bemessungswertSchwingbreite: result.Rd,
        ausnutzung: result.ausnutzungCalc,
      };
    }
    // #endregion

    // #endregion

    // #region Aussere
    let calcResultAussere = berechneAussere(
      schichtenCalculated,
      bemessungslast,
      bemessungspfahlwiderstand,
      pfahllaengeGewaehltInBoden,
      minimalePfahllaenge
    );

    let calcResultSchichten = berechneSchichten({
      schichtenInput: schichteninput,
      bohrkronendurchmesser: bohrkronendurchmesser,
      teilsicherheitsbeiwert: teilsicherheitsbeiwertAeussere,
      modellfaktor: modelFaktor,
      pfahlneigungZurVertikalen: pfahlneigungZurVertikalen,
      bemessungslast: Number(bemessungslast),
    });

    // #region Nachweis Druckpfahlgruppe erforderlich
    let dgruppe = {};
    if (frontendZustand.kompressionspfahlgruppenCheckbox) {
      dgruppe = {
        bemessungslastGesamtePfahlgruppe: bemessungslastGruppeGesamt,
        teilsicherheitsbeiwert: teilsicherheitsbeiwertAeussere,
        kantenlaengenErsatzpfahl: {
          la: la,
          lb: lb,
        },
        umfangErsatzpfahl: perimeterPileGroup,
        tabelle: {
          zeilenEingegeben: schichteninput,
          zeilenBerechnet: druckpfahlgruppe.schichtenCalculated,
          lreq: druckpfahlgruppe.lreq,
          sumRsdi: druckpfahlgruppe.sumRsdi,
        },
        erforderlichePfahllaenge: druckpfahlgruppe.lreq,
      };
    }
    // #endregion

    // #region Nachweis Uebertragungslaenge erforderlich
    let transmission = {};
    if (frontendZustand.uebertragungslaengeCheckbox) {
      let verbundfestigkeit = berechneVerbundfestigkeit(
        verbundbedingungenEta1,
        Number(traggliedDaten?.beiwert_eta2),
        druckfestigkeitZement,
        teilsicherheitsbeiwertTransmission
      );

      let uebertragungslaenge = berechneUebertragungslaenge(
        bemessungslast,
        Number(traggliedDaten?.durchmesser),
        verbundfestigkeit
      );

      let bondStrength = berechneBondStrength(
        verbundbedingungenEta1,
        Number(traggliedDaten?.beiwert_eta2),
        druckfestigkeitZement,
        teilsicherheitsbeiwertTransmission
      );

      transmission = {
        druckfestigkeitVerpressmoertelBeton: betonzugfestigkeit,
        betonzugfestigkeit: druckfestigkeitZement,
        teilsicherheitsbeiwert: teilsicherheitsbeiwertTransmission,
        verbundbedingungenGut: verbundbedingungenEta1,
        beiwert: Number(traggliedDaten?.beiwert_eta2),
        verbundfestigkeit: bondStrength,
        außendurchmesserStahltragglied: Number(traggliedDaten?.durchmesser),
        uebertragungslaenge: uebertragungslaenge,
      };
    }
    // #endregion

    // #region Nachweis Zugpfahlgruppe erforderlich
    let zugpfahlgruppe = {};
    if (frontendZustand.ueberpruefungVonPfahlgruppenCheckbox) {
      let gewichtkraftDesAngehaengtenBodens = berechneGewichtkraftDesAngehaengtenBodens(
        anzahlPfaehle,
        groesseresPfahlraster,
        kleineresPfahlraster,
        pfahllaengeGewaehltInBoden,
        reibungswinkelBoden,
        anpassungsfaktor,
        wichteBoden
      );

      let Lreq_calc = berechneLreq(
        bsp,
        staendigeDestabilisierendeEinwirkungen,
        veraenderlicheDestabilisierendeEinwirkungen,
        staendigeStabilisierendeEinwirkungen,
        kleineresPfahlraster,
        groesseresPfahlraster,
        wichteBoden,
        anpassungsfaktor,
        anzahlPfaehle,
        reibungswinkelBoden
      );

      let left = berechneLeft(
        staendigeDestabilisierendeEinwirkungen,
        bsp,
        veraenderlicheDestabilisierendeEinwirkungen
      );
      let right = berechneRight(
        staendigeStabilisierendeEinwirkungen,
        bsp,
        gewichtkraftDesAngehaengtenBodens
      );

      zugpfahlgruppe = {
        pfahllaenge: pfahllaengeGewaehltInBoden,
        groeßeresRastermaß: groesseresPfahlraster,
        kleineresRastermaß: kleineresPfahlraster,
        reibungswinkelBaugrund: reibungswinkelBoden,
        wichteBaugrund: wichteBoden,

        anzahlZugpfaehle: anzahlPfaehle,
        staendigeDestabilisierendeEinwirkungen: staendigeDestabilisierendeEinwirkungen,
        teilsicherheitsbeiwertDestabilisierendeStaendigeEinwirkungen: bsp?.YGD,
        veraenderlicheDestabilisierendeEinwirkungen: veraenderlicheDestabilisierendeEinwirkungen,
        teilsicherheitsbeiwertDestabilisierendeVeraenderlicheEinwirkungen: bsp?.YQD,
        staendigeStabilisierendeEinwirkungen: staendigeStabilisierendeEinwirkungen,
        teilsicherheitsbeiwertGuenstigeStaendigeEinwirkungen: bsp?.YGS,
        gewichtskraftAngehaengtenErdkoerper: gewichtkraftDesAngehaengtenBodens,
        anpassungsfaktor: anpassungsfaktor,
        left: left,
        right: right,
        erforderlichePfahllaenge: Lreq_calc,
      };
    }
    // #endregion
    // #endregion

    // #region PileHeadConstruction
    let zugHDPE = {};
    if (belastungsartUndRichtung === ZUGPFAHL) {
      let durchmesserDesRohres = berechneDurchmesserDesRohres(
        Number(traggliedDaten?.durchmesser),
        innereTragfaehigkeitsWerte.Csol
      );

      zugHDPE = {
        außendurchmesser: durchmesserDesRohres,
        wandstaerke: Number(traggliedDaten?.uebergangsrohr?.hdpe_rohr?.wanddicke),
        einbindelaengeFundament: traggliedDaten?.kopfkonstruktion?.einbindung_fundament,
        einbindelaengeBaugrund: traggliedDaten?.uebergangsrohr?.min_lb_zug,
        gesamtlaenge: traggliedDaten?.uebergangsrohr.laenge_zug,
        kantenlaenge: traggliedDaten?.kopfkonstruktion?.kantenlaenge,
        plattendicke: traggliedDaten?.kopfkonstruktion?.dicke,
      };
    }

    let druckHDPE = {};
    if (belastungsartUndRichtung === DRUCKPFAHL && werkstoffUebergangsrohr == 0) {
      let innereTragfaehigkeitsWerte = BerechneInnereTragfaehigkeit({
        bemessungenMikropfaehle,
        bemessungssituation,
        tragglied,
        bemessungslast,
        bohrkronendurchmesser,
        belastungsartUndRichtung,
        traggliedDaten: traggliedDaten,
        teilsicherheitsbeiwert: teilsicherheitsbeiwertTragglied,
      });

      let durchmesserDesRohres = innereTragfaehigkeitsWerte?.durchmesserDesRohres;
      let minimalerRandabstand = innereTragfaehigkeitsWerte?.minimalerRandabstand;

      let achsabstand = berechneAchsabstand(
        durchmesserDesRohres,
        traggliedDaten?.kopfkonstruktion?.kantenlaenge
      );

      let c = berechneC(durchmesserDesRohres, traggliedDaten?.kopfkonstruktion.kantenlaenge);

      druckHDPE = {
        außendurchmesser: durchmesserDesRohres,
        wandstaerke: Number(traggliedDaten?.uebergangsrohr?.hdpe_rohr?.wanddicke),
        einbindelaengeFundament: traggliedDaten?.kopfkonstruktion?.einbindung_fundament,
        einbindelaengeBaugrund: traggliedDaten?.uebergangsrohr?.min_lb_druck,
        gesamtlaenge: traggliedDaten?.uebergangsrohr.laenge_druck,
        kantenlaenge: traggliedDaten?.kopfkonstruktion?.kantenlaenge,
        plattendicke: traggliedDaten?.kopfkonstruktion?.dicke,
        abstandUebergangsrohr: traggliedDaten?.kopfkonstruktion?.abstand_uebergangsrohr,
        randabstand: minimalerRandabstand,
        achsabstand: achsabstand,
        ObereZusatzbewehrung: {
          phi: traggliedDaten?.obere_zusatzbewehrung.durchmesser,
          n: traggliedDaten?.obere_zusatzbewehrung.n,
          eMin: achsabstand,
          b: durchmesserDesRohres,
        },
        UntereZusatzbewehrung: {
          phi: traggliedDaten?.untere_zusatzbewehrung.durchmesser,
          n: traggliedDaten?.untere_zusatzbewehrung.n,
          c: c,
        },
      };
    }

    let druckStahl = {};
    if (belastungsartUndRichtung === DRUCKPFAHL && werkstoffUebergangsrohr == 1) {
      let innereTragfaehigkeitsWerte = BerechneInnereTragfaehigkeit({
        bemessungenMikropfaehle,
        bemessungssituation,
        tragglied,
        bemessungslast,
        bohrkronendurchmesser,
        belastungsartUndRichtung,
        traggliedDaten: traggliedDaten,
        teilsicherheitsbeiwert: teilsicherheitsbeiwertTragglied,
      });

      let wandDicke = berechneWandDicke(
        bemessungssituation,
        traggliedDaten?.uebergangsrohr?.stahlrohr?.wanddicke
      );

      druckStahl = {
        außendurchmesser: innereTragfaehigkeitsWerte?.durchmesserDesRohres,
        wandstaerke: Number(wandDicke),
        einbindelaengeFundament: traggliedDaten?.kopfkonstruktion?.einbindung_fundament,
        einbindelaengeBaugrund: traggliedDaten?.uebergangsrohr?.min_lb_druck,
        gesamtlaenge: traggliedDaten?.uebergangsrohr.laenge_druck,
        kantenlaenge: traggliedDaten?.kopfkonstruktion?.kantenlaenge,
        plattendicke: traggliedDaten?.kopfkonstruktion?.dicke,
        abstandUebergangsrohr: traggliedDaten?.kopfkonstruktion?.abstand_uebergangsrohr,
      };
    }
    // #endregion
    return {
      Deckblatt: {
        Project: project,
        PreparedBy: preparedBy,
        BemessungenMikropfaehle: bemessungenMikropfaehle,
        LogoImageUrl: logoImageBase64Url,
      },
      Germany: {
        Innere: {
          BeanspruchungausStatik: {
            belastungsartUndRichtung: belastungsartUndRichtung,
            bemessungssituation: bemessungssituation,
            BemessungslastAusCharakteristischenEinwirkungen: bemessungslastBerechnet,
            bemessungslast: bemessungslast,
          },

          NachweisStahltragglied: {
            wahlDesStahltraggliedes: tragglied.title,
            erforderlicheZementsteinueberdeckung: innereTragfaehigkeitsWerte.Csol,
            bohrkronendurchmesser: bohrkronendurchmesser,
            durchmesserStahltragglied: Number(traggliedDaten?.durchmesser),
            aufweitungNnachDIN_SPEC_18539: aufweitungNnachDIN_SPEC_18539,
            vorhandeneZementsteinueberdeckung: innereTragfaehigkeitsWerte.Cist,
            charakteristischeTragfaehigkeit: innereTragfaehigkeitsWerte.Rmax,
            teilsicherheitsbeiwert: teilsicherheitsbeiwertTragglied,
            bemessungswertDerTragfaehigkeit: innereTragfaehigkeitsWerte.Rcd,
            ausnutzung: innereTragfaehigkeitsWerte.fixedN,
          },

          // Knicken
          Knicken: KnickenResults,

          // Ermuedung
          Ermuedung: ermuedungResults,
        },
        Aussere: {
          NachweisMantelreibung: {
            bemessungslast: bemessungslast,
            gewaehltesStahltragglied: tragglied.title,
            belastungsartRichtung: belastungsartUndRichtung,
            bohrerdurchmesser: bohrkronendurchmesser,
            pfahlneigungVertikalen: pfahlneigungZurVertikalen,
            teilsicherheitsbeiwert: teilsicherheitsbeiwertAeussere,
            modelFaktor: modelFaktor,
          },
          Bodenschichten: {
            table_input: schichteninput,
            table_calulated: schichtenCalculated,
            lreq: calcResultSchichten.resultSumLi,
            sumRsdi: calcResultSchichten.resultSumRsdi,
            erforderlichePfahllaenge: minimalePfahllaenge,
            gewaehltePfahllaenge: pfahllaengeGewaehltInBoden,
            bemessungswertPfahlwiderstandes: calcResultAussere?.designedPileResistance,
            ausnutzung: calcResultAussere?.utilizationFactor,
          },
          BestimmungGesamtlaenge: {
            ueberstand: pileCapExtension,
            pfahllaenge: longestPfahlValue,
            gesamtePfahllaenge: longestPfahlValuePlusExtension,
          },

          NachweisDruckpfahlgruppe: dgruppe,
          NachweisUebertragungslaenge: transmission,
          NachweisZugpfahlgruppe: zugpfahlgruppe,
        },
        PileHeadConstruction: {
          belastungsartUndRichtung: belastungsartUndRichtung,
          werkstoffUebergangsrohr: werkstoffUebergangsrohr,
          bemessungslast: bemessungslast,
          wahlDesStahltraggliedes: tragglied.title,
          durchmesserStahltragglied: Number(traggliedDaten?.durchmesser),
          vorhandeneZementsteinueberdeckung: innereTragfaehigkeitsWerte.Csol,

          // Zugpfahl
          ZugHDPE: zugHDPE,

          // Druckpfahl
          DruckHDPE: druckHDPE,
          DruckStahl: druckStahl,
        },
      },
      Aashto: {
        aashtoAll: aashtoAll,
      },
      Fhwa: {
        fhwaAll: fhwaAll,
      },
      Edp: {
        edpAll: edpAll,
      },
      FrontendZustand: frontendZustand,
    };
  }
);
